import {
  Stack,
  Typography,
  Grid,
  Paper,
  Divider,
  Box,
  IconButton,
  Button,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { CircularLoading } from "@/components/common";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { usePostAttendanceContext } from "@/contexts/PostAttendanceContext";
import { toast } from "react-toastify";
import { extensionService } from "@/services/subscription/ExtensionService";
import { resolveCrmOrganizationId } from "src-next/modules/crm-clinical/communications/helpers/resolveCrmOrganizationId";
import { disconnectMetaOnFluxZapExtensionDeactivated } from "src-next/modules/crm-clinical/setup/services/disconnectMetaOnFluxZapExtensionDeactivated";

import { PreapprovalResponse } from "../../register/types";
import CheckoutForm from "@/pages/register/components/CheckoutForm";
import { FluxZapDeactivationNotice } from "./FluxZapDeactivationNotice";

interface PaymentPostAttendanceProps {
  mode: "activation" | "deactivation";
  onGoBack: () => void;
  postAttendancePlan?: PreapprovalResponse;
  isLoading?: boolean;
}

export function PaymentPostAttendance({
  mode,
  onGoBack,
  postAttendancePlan,
  isLoading = false,
}: PaymentPostAttendanceProps) {
  const { organization } = useOrganizationContext();

  const { contractPostAttendance, refetchContract } =
    usePostAttendanceContext();
  const extensionServiceInstance = new extensionService();

  // Função para formatar valores monetários
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  // Função para desativar o pós-atendimento
  const handleDeactivatePostAttendance = async () => {
    try {
      if (!contractPostAttendance?.id) {
        toast.error("Contrato não encontrado");
        return;
      }

      await extensionServiceInstance.unsubscribeContract(
        contractPostAttendance.id,
      );

      try {
        const metaDisconnected =
          await disconnectMetaOnFluxZapExtensionDeactivated({
            organizationId: resolveCrmOrganizationId(organization?.id),
          });
        if (metaDisconnected) {
          toast.info(
            "Conexão Meta desativada. O envio de mensagens voltará a usar o número padrão Fluxmed e os pacotes contratados.",
          );
        }
      } catch (metaDisconnectError) {
        console.error(
          "Erro ao desativar canal Meta após cancelar FluxZap:",
          metaDisconnectError,
        );
      }

      await refetchContract();
      toast.success("Extensão desativada com sucesso!");
      onGoBack();
    } catch (error) {
      console.error("Erro ao desativar extensão:", error);
      toast.error("Erro ao desativar extensão. Tente novamente.");
    }
  };

  // Renderiza a tela de ativação do pós-atendimento
  const renderActivation = () => {
    if (isLoading) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          height="100%"
          padding={4}
        >
          <CircularLoading />
          <Typography variant="body1" mt={2}>
            Carregando informações do plano...
          </Typography>
        </Stack>
      );
    }

    if (!postAttendancePlan) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          height="100%"
          padding={4}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Não foi possível carregar o plano FluxZap
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mb: 2, width: "80%" }}
          >
            Verifique sua conexão com a internet ou entre em contato com o
            suporte.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={onGoBack}
            startIcon={<ArrowBack />}
            sx={{ mt: 2, borderRadius: 12 }}
          >
            Voltar para Extensões
          </Button>
        </Stack>
      );
    }

    return (
      <>
        <Typography
          variant="body1"
          fontSize="1.1rem"
          color="neutral700.main"
          align="center"
          gutterBottom
          sx={{ mb: 3 }}
        >
          Para ativar o módulo FluxZap, por favor complete o pagamento abaixo:
        </Typography>

        <Box
          sx={{
            width: "100%",
            flexGrow: 1,
            display: "flex",
            margin: "0 auto",
            mt: 3,
          }}
        >
          <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
            {/* Coluna da esquerda - Informações do plano */}
            <Grid item xs={12} md={4}>
              <Paper
                elevation={3}
                sx={{ p: 3, borderRadius: 2, height: "100%" }}
              >
                <Typography
                  variant="h6"
                  color="primary"
                  fontWeight="700"
                  mb={3}
                  align="center"
                >
                  Resumo do Plano
                </Typography>
                <Stack spacing={3}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography
                      variant="body1"
                      fontWeight="500"
                      color="text.secondary"
                    >
                      Nome do Serviço:
                    </Typography>
                    <Typography variant="body1" fontWeight="700">
                      {postAttendancePlan.reason || "Pós-atendimento"}
                    </Typography>
                  </Stack>
                  <Divider />

                  <Stack direction="row" justifyContent="space-between">
                    <Typography
                      variant="body1"
                      fontWeight="500"
                      color="text.secondary"
                    >
                      Valor Mensal:
                    </Typography>
                    <Typography variant="body1" fontWeight="700">
                      {formatCurrency(
                        postAttendancePlan.auto_recurring.transaction_amount,
                      )}
                    </Typography>
                  </Stack>
                  <Divider />

                  {postAttendancePlan.auto_recurring.billing_day && (
                    <>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography
                          variant="body1"
                          fontWeight="500"
                          color="text.secondary"
                        >
                          Data de Cobrança:
                        </Typography>
                        <Typography variant="body1" fontWeight="700">
                          Todo dia{" "}
                          {postAttendancePlan.auto_recurring.billing_day}
                        </Typography>
                      </Stack>
                      <Divider />
                    </>
                  )}

                  <Stack direction="row" justifyContent="space-between">
                    <Typography
                      variant="body1"
                      fontWeight="500"
                      color="text.secondary"
                    >
                      Organização:
                    </Typography>
                    <Typography variant="body1" fontWeight="700">
                      {organization?.name || "Sua organização"}
                    </Typography>
                  </Stack>
                  <Divider />

                  <Stack direction="row" justifyContent="space-between">
                    <Typography
                      variant="body1"
                      fontWeight="500"
                      color="text.secondary"
                    >
                      Benefícios:
                    </Typography>
                    <Typography variant="body1" fontWeight="700">
                      Integração com WhatsApp
                    </Typography>
                  </Stack>
                  <Divider />

                  <Typography variant="body1" color="text.secondary">
                    Inclui 20 mensagens gratuitas com o número de WhatsApp
                    padrão do Fluxmed (até a ativação do seu número de
                    WhatsApp).
                  </Typography>
                  <Divider />
                </Stack>
              </Paper>
            </Grid>

            {/* Coluna da direita - Formulário de checkout */}
            <Grid item xs={12} md={8}>
              <Paper
                elevation={3}
                sx={{ p: 4, borderRadius: 2, height: "100%" }}
              >
                <CheckoutForm
                  selectedPlan={postAttendancePlan}
                  enableCrm20TrialOnSuccess
                />
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </>
    );
  };

  // Renderiza a tela de desativação do pós-atendimento
  const renderDeactivation = () => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          flexGrow: 1,
          width: "100%",
        }}
      >
        <Paper
          elevation={3}
          sx={{ p: 6, borderRadius: 2, maxWidth: 700, width: "100%" }}
        >
          <Stack spacing={3} alignItems="center" sx={{ width: "100%" }}>
            <Typography variant="h6" align="center" fontWeight="600">
              Tem certeza que deseja cancelar o serviço de Pós-atendimento?
            </Typography>

            <FluxZapDeactivationNotice organizationId={organization?.id} />

            <Typography
              variant="subtitle1"
              align="center"
              fontWeight="600"
              color="error.main"
            >
              Organização: {organization?.name || "Sua organização"}
            </Typography>

            <Stack
              direction="row"
              spacing={3}
              justifyContent="center"
              sx={{ mt: 2 }}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={onGoBack}
                sx={{ width: 250, height: 45, borderRadius: 2 }}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeactivatePostAttendance}
                sx={{
                  width: 250,
                  height: 45,
                  fontWeight: "bold",
                  borderRadius: 2,
                }}
              >
                Confirmar Cancelamento
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  };

  return (
    <Stack width="100%" height="100%" spacing={3} padding={4}>
      <Stack direction="row" alignItems="center" spacing={2} mb={4}>
        <IconButton onClick={onGoBack} sx={{ p: 1 }}>
          <ArrowBack fontSize="large" />
        </IconButton>
        <Typography
          variant="h5"
          fontWeight="600"
          align="center"
          sx={{ flexGrow: 1, mr: 5 }}
        >
          {mode === "activation" ? "Ativar FluxZap" : "Cancelar FluxZap"}
        </Typography>
      </Stack>

      {mode === "activation" ? renderActivation() : renderDeactivation()}
    </Stack>
  );
}
